import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faPlus,
  faImage,
  faTrash,
  faPen,
  faSpinner,
  faCheck,
  faTimes,
  faChevronLeft,
  faExpand,
  faComments,
  faCopy,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { aiChatAPI } from '../services/api';
import '../styles/AiAssistant.css';
import '../styles/AiAssistant-LightMode.css';
import useI18n from '../hooks/useI18n';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  imageUrls: string[];
  createdAt: string;
}

interface Chat {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages?: ChatMessage[];
  _count?: { messages: number };
}

const AiAssistant: React.FC = () => {
  const { t } = useI18n();
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deleteChatId, setDeleteChatId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { loadChats(); }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
    }
  }, [input]);

  // Scroll detection for "scroll to bottom" button
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      setShowScrollBtn(!isNearBottom && messages.length > 3);
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChats = async () => {
    try {
      setLoading(true);
      const res = await aiChatAPI.getChats();
      setChats(res.data.chats || []);
    } catch (error) {
      console.error('Failed to load chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewChat = async () => {
    try {
      const res = await aiChatAPI.createChat();
      const newChat = res.data.chat;
      setChats(prev => [newChat, ...prev]);
      setActiveChat(newChat);
      setMessages([]);
      setInput('');
      setImages([]);
      setImagePreviews([]);
    } catch (error) {
      console.error('Failed to create chat:', error);
    }
  };

  const loadChat = useCallback(async (chatId: string) => {
    try {
      const res = await aiChatAPI.getChat(chatId);
      const chat = res.data.chat;
      setActiveChat(chat);
      setMessages(chat.messages || []);
    } catch (error) {
      console.error('Failed to load chat:', error);
    }
  }, []);

  const deleteChat = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteChatId(chatId);
  };

  const confirmDeleteChat = async () => {
    if (!deleteChatId) return;
    try {
      await aiChatAPI.deleteChat(deleteChatId);
      setChats(prev => prev.filter(c => c.id !== deleteChatId));
      if (activeChat?.id === deleteChatId) {
        setActiveChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    } finally {
      setDeleteChatId(null);
    }
  };

  const startRenaming = (chatId: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingChatId(chatId);
    setEditTitle(currentTitle);
  };

  const saveRename = async (chatId: string) => {
    if (!editTitle.trim()) return;
    try {
      await aiChatAPI.renameChat(chatId, editTitle.trim());
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, title: editTitle.trim() } : c));
      if (activeChat?.id === chatId) {
        setActiveChat(prev => prev ? { ...prev, title: editTitle.trim() } : null);
      }
    } catch (error) {
      console.error('Failed to rename chat:', error);
    }
    setEditingChatId(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + images.length > 5) {
      alert(t('ai.maxImages'));
      return;
    }
    setImages(prev => [...prev, ...files]);
    const previews = files.map(f => URL.createObjectURL(f));
    setImagePreviews(prev => [...prev, ...previews]);
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const copyToClipboard = (content: string, msgId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(msgId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const sendMessage = async () => {
    if (!input.trim() && images.length === 0) return;
    if (sending) return;

    let chatId = activeChat?.id;

    if (!chatId) {
      try {
        const res = await aiChatAPI.createChat();
        const newChat = res.data.chat;
        chatId = newChat.id;
        setActiveChat(newChat);
        setChats(prev => [newChat, ...prev]);
      } catch {
        return;
      }
    }

    const userContent = input.trim();
    const userImages = [...images];
    const userPreviews = [...imagePreviews];

    const tempUserMsg: ChatMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: userContent,
      imageUrls: userPreviews,
      createdAt: new Date().toISOString(),
    };
    const tempAiMsg: ChatMessage = {
      id: `temp-ai-${Date.now()}`,
      role: 'assistant',
      content: '',
      imageUrls: [],
      createdAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMsg, tempAiMsg]);
    setInput('');
    setImages([]);
    setImagePreviews([]);
    setSending(true);

    try {
      const res = await aiChatAPI.sendMessage(chatId!, userContent, userImages.length > 0 ? userImages : undefined);
      const { userMessage, assistantMessage } = res.data;
      setMessages(prev =>
        prev.map(m => {
          if (m.id === tempUserMsg.id) return userMessage;
          if (m.id === tempAiMsg.id) return assistantMessage;
          return m;
        })
      );
      if (chats.length > 0) loadChats();
    } catch (error: any) {
      setMessages(prev =>
        prev.map(m =>
          m.id === tempAiMsg.id
            ? { ...m, content: `⚠️ Error: ${error.response?.data?.error || 'Failed to get response. Please try again.'}` }
            : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content: string) => {
    return content
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code class="lang-$1">$2</code></pre>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^### (.*$)/gm, '<h4>$1</h4>')
      .replace(/^## (.*$)/gm, '<h3>$1</h3>')
      .replace(/^# (.*$)/gm, '<h2>$1</h2>')
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      .replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>')
      .replace(/\n/g, '<br/>');
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="grok-assistant">
      {/* ── Mobile Sidebar Backdrop ── */}
      {sidebarOpen && (
        <div className="grok-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`grok-sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
        <div className="grok-sidebar-top">
          <div className="grok-brand">
            <div className="grok-brand-icon">E</div>
            <span className="grok-brand-text">ELVOAQ AI</span>
          </div>
          <button className="grok-new-btn" onClick={() => { createNewChat(); setSidebarOpen(false); }} title="New Conversation">
            <FontAwesomeIcon icon={faPlus} />
            <span>{t('ai.newChat')}</span>
          </button>
        </div>

        <div className="grok-chat-list">
          {loading && chats.length === 0 && (
            <div className="grok-sidebar-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}
          {!loading && chats.length === 0 && (
            <div className="grok-empty-sidebar">
              <FontAwesomeIcon icon={faComments} />
              <p>{t('ai.noChats')}</p>
              <span>{t('ai.startChat')}</span>
            </div>
          )}
          {chats.map(chat => (
            <div
              key={chat.id}
              className={`grok-chat-item ${activeChat?.id === chat.id ? 'active' : ''}`}
              onClick={() => { loadChat(chat.id); setSidebarOpen(false); }}
            >
              {editingChatId === chat.id ? (
                <div className="grok-chat-edit">
                  <input
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveRename(chat.id)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                  />
                  <button className="grok-edit-save" onClick={(e) => { e.stopPropagation(); saveRename(chat.id); }}>
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button className="grok-edit-cancel" onClick={(e) => { e.stopPropagation(); setEditingChatId(null); }}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="grok-chat-item-icon">
                    <FontAwesomeIcon icon={faComments} />
                  </div>
                  <div className="grok-chat-item-info">
                    <span className="grok-chat-item-title">{chat.title}</span>
                    <span className="grok-chat-item-date">
                      {new Date(chat.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="grok-chat-item-actions">
                    <button onClick={(e) => startRenaming(chat.id, chat.title, e)} title="Rename">
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button onClick={(e) => deleteChat(chat.id, e)} title="Delete" className="grok-delete-btn">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="grok-main">
        {/* Header */}
        <header className="grok-header">
          <div className="grok-header-left">
            <button className="grok-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <FontAwesomeIcon icon={sidebarOpen ? faChevronLeft : faExpand} />
            </button>
            <div className="grok-header-title">
              <div className="grok-header-icon">E</div>
              <h1>{activeChat?.title || 'ELVOAQ AI Assistant'}</h1>
            </div>
          </div>
          <div className="grok-header-right">
            <div className="grok-status-dot" />
            <span className="grok-status-text">Online</span>
          </div>
        </header>

        {/* Messages */}
        <div className="grok-messages" ref={messagesContainerRef}>
          {messages.length === 0 && (
            <div className="grok-welcome">
              <div className="grok-welcome-glow" />
              <div className="grok-welcome-icon">E</div>
              <h2>ELVOAQ AI Assistant</h2>
              <p>Analyze items, estimate values, get smart collection insights, and more.</p>
              <div className="grok-suggestions">
                <button onClick={() => setInput('What items in my collection are the most valuable?')}>
                  <span className="grok-sug-emoji">💰</span>
                  <span className="grok-sug-text">Most valuable items</span>
                </button>
                <button onClick={() => setInput('Suggest how to better organize my collection')}>
                  <span className="grok-sug-emoji">📂</span>
                  <span className="grok-sug-text">Organization tips</span>
                </button>
                <button onClick={() => setInput('What categories should I create for my items?')}>
                  <span className="grok-sug-emoji">🏷️</span>
                  <span className="grok-sug-text">Category suggestions</span>
                </button>
                <button onClick={() => setInput('Help me estimate insurance value for my collection')}>
                  <span className="grok-sug-emoji">🛡️</span>
                  <span className="grok-sug-text">Insurance estimation</span>
                </button>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`grok-msg ${msg.role}`}>
              <div className="grok-msg-avatar">
                {msg.role === 'user' ? (
                  <div className="grok-avatar-user">U</div>
                ) : (
                  <div className="grok-avatar-ai">E</div>
                )}
              </div>
              <div className="grok-msg-body">
                <div className="grok-msg-meta">
                  <span className="grok-msg-sender">{msg.role === 'user' ? 'You' : 'ELVOAQ AI'}</span>
                  <span className="grok-msg-time">{formatTime(msg.createdAt)}</span>
                </div>
                {msg.imageUrls && msg.imageUrls.length > 0 && (
                  <div className="grok-msg-images">
                    {msg.imageUrls.map((url, i) => (
                      <img key={i} src={url} alt={`Attached ${i + 1}`} />
                    ))}
                  </div>
                )}
                {msg.content ? (
                  <div className="grok-msg-bubble">
                    <div
                      className="grok-msg-text"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                    {msg.role === 'assistant' && (
                      <button
                        className="grok-copy-btn"
                        onClick={() => copyToClipboard(msg.content, msg.id)}
                        title="Copy response"
                      >
                        <FontAwesomeIcon icon={copiedId === msg.id ? faCheck : faCopy} />
                        {copiedId === msg.id && <span>Copied!</span>}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grok-msg-bubble grok-thinking">
                    <div className="grok-thinking-dots">
                      <span /><span /><span />
                    </div>
                    <span className="grok-thinking-text">Grok is thinking...</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom */}
        {showScrollBtn && (
          <button className="grok-scroll-btn" onClick={scrollToBottom}>
            <FontAwesomeIcon icon={faArrowDown} />
          </button>
        )}

        {/* Input */}
        <div className="grok-input-area">
          <div className="grok-input-container">
            {imagePreviews.length > 0 && (
              <div className="grok-previews">
                {imagePreviews.map((preview, i) => (
                  <div key={i} className="grok-preview-item">
                    <img src={preview} alt={`Preview ${i + 1}`} />
                    <button className="grok-preview-remove" onClick={() => removeImage(i)}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="grok-input-row">
              <button
                className="grok-attach"
                onClick={() => fileInputRef.current?.click()}
                title="Attach images for analysis"
              >
                <FontAwesomeIcon icon={faImage} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask ELVOAQ AI..."
                rows={1}
                disabled={sending}
              />
              <button
                className={`grok-send ${input.trim() || images.length > 0 ? 'active' : ''}`}
                onClick={sendMessage}
                disabled={sending || (!input.trim() && images.length === 0)}
              >
                {sending ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPaperPlane} />
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Modal */}
      {deleteChatId && (
        <div className="grok-overlay" onClick={() => setDeleteChatId(null)}>
          <div className="grok-modal" onClick={e => e.stopPropagation()}>
            <div className="grok-modal-icon">
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <h3>Delete Conversation</h3>
            <p>This conversation will be permanently deleted. This action cannot be undone.</p>
            <div className="grok-modal-actions">
              <button className="grok-modal-cancel" onClick={() => setDeleteChatId(null)}>
                Cancel
              </button>
              <button className="grok-modal-confirm" onClick={confirmDeleteChat}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiAssistant;
